import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUserId } from 'redux/reducers/authReducer';

const STORAGE_PREFIX = 'relatorios-dessem-niveis-contingencia-aberto-v1';

/**
 * Chave do quadro no localStorage, uma por usuário e versionada: `<prefixo>:<userId>`. Sem usuário
 * válido (`selectUserId` devolve -1 sem JWT), `anon`.
 */
export function niveisContingenciaStorageKey(userId: number): string {
  const dono = Number.isSafeInteger(userId) && userId > 0 ? String(userId) : 'anon';
  return `${STORAGE_PREFIX}:${dono}`;
}

/**
 * Lê a preferência; qualquer falha (storage bloqueado, JSON inválido, formato errado) vale
 * fechado.
 */
function lerAberto(chave: string): boolean {
  try {
    const bruto = window.localStorage.getItem(chave);
    if (!bruto) return false;
    const valor: unknown = JSON.parse(bruto);
    return typeof valor === 'object' && valor !== null
      && (valor as Record<string, unknown>).aberto === true;
  } catch {
    return false;
  }
}

/** Grava a preferência; se o storage recusar (bloqueado, cheio), ela fica só em memória. */
function gravarAberto(chave: string, aberto: boolean): void {
  try {
    window.localStorage.setItem(chave, JSON.stringify({ aberto }));
  } catch {
    // Sem storage o estado vale até sair da tela: é só uma preferência de leitura.
  }
}

/**
 * Aberto/fechado do quadro "Níveis de Contingência" (Premissas Gerais do DESSEM), lembrado por
 * usuário no navegador.
 *
 * Fica no localStorage (`relatorios-dessem-niveis-contingencia-aberto-v1:<userId>`, valor
 * `{ "aberto": true|false }`) porque é preferência de leitura da tela, não dado de negócio: não
 * precisa de rota nem de sincronismo entre dispositivos, e perdê-la (storage limpo ou bloqueado)
 * custa um clique. A leitura é síncrona no 1º render (`useState` com inicializador), para o quadro
 * já nascer no estado certo, sem piscar; começa fechado quando não há nada salvo ou a leitura
 * falha. Toda leitura e escrita vai em `try/catch`: storage indisponível nunca quebra a tela.
 *
 * @returns `[aberto, alternar]`: o estado e a ação de abrir/fechar (que também grava).
 */
export default function useNiveisContingenciaAberto(): [boolean, () => void] {
  const userId = useSelector(selectUserId);
  const chave = niveisContingenciaStorageKey(userId);
  const [aberto, setAberto] = useState(() => lerAberto(chave));

  const alternar = useCallback(() => {
    const proximo = !aberto;
    gravarAberto(chave, proximo);
    setAberto(proximo);
  }, [aberto, chave]);

  return [aberto, alternar];
}
